import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import { Card, Button, Badge, Spinner, StatCard } from '../../components/UI';
import { usersAPI, callLogsAPI, tasksAPI } from '../../services/api';
import {
  HiOutlineArrowLeft, HiOutlinePencil, HiOutlinePhone,
  HiOutlineMail, HiOutlineClipboardList, HiOutlineTrendingUp
} from 'react-icons/hi';
import { format } from 'date-fns';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch user details
  const { data: user, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      const response = await usersAPI.getById(id);
      return response.data?.data || null;
    },
    enabled: !!id,
  });

  // Fetch user's call logs
  const { data: callLogs } = useQuery({
    queryKey: ['userCallLogs', id],
    queryFn: async () => {
      const response = await callLogsAPI.getByUser(id, { limit: 10 });
      return response.data?.data || [];
    },
    enabled: !!id,
  });

  // Fetch user's performance
  const { data: performance } = useQuery({
    queryKey: ['userPerformance', id],
    queryFn: async () => {
      const response = await usersAPI.getPerformance(id);
      return response.data?.data || {};
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="p-4 text-center">
          <p className="text-gray-500">User not found</p>
          <Button variant="primary" onClick={() => navigate('/app/users')} className="mt-4">
            Back to Users
          </Button>
        </div>
      </Layout>
    );
  }

  const stats = performance || {};

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              icon={<HiOutlineArrowLeft className="w-5 h-5" />}
              onClick={() => navigate('/app/users')}
            />
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-semibold text-lg">
                  {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                </span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {user.firstName} {user.lastName}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={user.role === 'admin' ? 'primary' : 'default'}>
                    {user.role}
                  </Badge>
                  <Badge variant={user.isActive !== false ? 'success' : 'danger'}>
                    {user.isActive !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={<HiOutlinePencil className="w-4 h-4" />}
          >
            Edit
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Calls"
            value={stats.totalCalls || 0}
            icon={<HiOutlinePhone className="w-6 h-6 text-white" />}
            color="primary"
          />
          <StatCard
            title="Connected"
            value={stats.connectedCalls || 0}
            icon={<HiOutlineTrendingUp className="w-6 h-6 text-white" />}
            color="success"
          />
          <StatCard
            title="Leads Closed"
            value={stats.leadsClosed || 0}
            icon={<HiOutlineClipboardList className="w-6 h-6 text-white" />}
            color="info"
          />
          <StatCard
            title="Conversion Rate"
            value={`${stats.conversionRate || 0}%`}
            icon={<HiOutlineTrendingUp className="w-6 h-6 text-white" />}
            color="warning"
          />
        </div>

        {/* User Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card padding="p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <HiOutlineMail className="w-5 h-5 text-gray-400" />
                <span className="text-gray-600">{user.email}</span>
              </div>
              {/* Multiple Phone Numbers */}
              {(user.phones || []).length > 0 && (
                <div className="space-y-2">
                  {user.phones.map((phone, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <HiOutlinePhone className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-600">{phone.number}</span>
                      <Badge variant="default" size="sm">{phone.label}</Badge>
                      {phone.isDefault && (
                        <Badge variant="success" size="sm">Default</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {user.department && (
                <div className="flex items-center gap-3">
                  <HiOutlineClipboardList className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-600">{user.department}</span>
                </div>
              )}
              {user.designation && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">Designation:</span>
                  <span className="text-gray-600">{user.designation}</span>
                </div>
              )}
            </div>
          </Card>

          <Card padding="p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Recent Activity</h2>
            {callLogs && callLogs.length > 0 ? (
              <div className="space-y-3">
                {callLogs.slice(0, 5).map((log) => (
                  <div key={log._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <p className="font-medium text-gray-900">{log.phoneNumber}</p>
                      <p className="text-sm text-gray-500">
                        {log.createdAt ? format(new Date(log.createdAt), 'MMM d, h:mm a') : '-'}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant={log.status === 'connected' ? 'success' : 'danger'} size="sm">
                        {log.status}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        {log.duration ? `${Math.floor(log.duration / 60)}m` : '-'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default UserDetails;